import { selectorFamily, selector } from 'recoil';
import jwt_decode from 'jwt-decode';
import cookie from 'react-cookies';

import { twoFactorLogin, userInfo, refetchToken, forceLogin, updateUser } from '../Atoms/Login';

import { api } from '../../Services/api';
import { sendSignInLinkToEmail, isSignInWithEmailLink } from '../../Services/firebase';
import { storage, session } from '../../Services/storage';

import { decodeQueryString } from '../../Common/decodeQueryString';

const checkAuthType = selectorFamily({
  key: 'checkAuthType',
  get: (email: string) => async ({get}) => {
    try {
      get(forceLogin);
      const res: any = await api.POST('auth/client/auth-type', { email });
      session.set('type', res.authenticationType);
      return {
        result: res,
        success: true
      };
    } catch(err) {
      return {
        result: err,
        success: false
      };
    }
  }
});

const loginWithPassword = selectorFamily({
  key: 'loginWithPassword',
  get: ({ email, password }: any) => async ({ get }) => {
    try {
      get(forceLogin);
      const res: any = await api.POST('auth/client/login', { email, password });
      storage.set('user', JSON.stringify(res));
      storage.set('refreshToken', res.refreshToken);
      cookie.save('accessToken', res.token, {
        path: '/',
        expires: new Date(Date.now() + (5 * 60 * 1000))
      });

      return {
        result: res,
        success: true
      };
    } catch(err) {
      return {
        result: err,
        success: false
      };
    }
  }
});

const loginWithoutPassword = selector({
  key: 'loginWithoutPassword',
  get: async ({ get }) => {
    get(forceLogin);
    const value = get(twoFactorLogin);
    const email = storage.get('userEmail');
    if(email && value === 1) {
      try {
        const res: any = await api.POST('auth/client/login', { email });

        storage.remove('userEmail');
        storage.set('user', JSON.stringify(res.user));
        storage.set('refreshToken', res.refreshToken);
        cookie.save('accessToken', res.token, {
          path: '/',
          expires: new Date(Date.now() + (5 * 60 * 1000))
        });

        return {
          result: res,
          success: true
        };
      } catch(err) {
        return {
          result: err,
          success: false
        };
      }
    } else {
      return null;
    }
  }
});

const refreshAuthToken = selector({
  key: 'refreshAuthToken',
  get: async ({ get }) => {
    const refetchTokenValue = get(refetchToken);
    const refreshToken = storage.get('refreshToken');
    if(refetchTokenValue !== 0 && refreshToken) {
      try {
        const res: any = await api.POST(
          'auth/client/token',
          { refreshToken }
        );

        cookie.save('accessToken', res.token, {
          path: '/',
          expires: new Date(Date.now() + (5 * 60 * 1000))
        });

        return {
          result: res,
          success: true
        };
      } catch(err) {

        return {
          result: err,
          success: false
        };
      }
    }

    return null;
  }
});

const removeUser = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('refreshToken');
  cookie.remove('accessToken', { path: '/' });
  window.location.reload()
}

const verifyAuthToken = async () => {
  const accessToken = cookie.load('accessToken')
  const refreshToken = storage.get('refreshToken');

  let needsRefresh = false;

  if (accessToken) {
    try {
      const decodedToken: any = jwt_decode(accessToken);
      const { exp } = decodedToken
      // Check if token is expired (not if duration is > 10 minutes!)
      const currentTime = Date.now() / 1000; // Current time in seconds
      if (currentTime >= exp) {
        console.log('Access token expired, will try to refresh');
        needsRefresh = true;
      } else {
        console.log('Access token is valid');
        return; // Token is valid, no need to refresh
      }
    } catch (error) {
      console.error('Token decode failed:', error);
      needsRefresh = true; // If we can't decode it, try to refresh
    }
  } else {
    needsRefresh = true; // No access token, need to refresh
  }

  // Try to refresh the token if needed
  if (needsRefresh) {
    if (!refreshToken) {
      console.error('No refresh token available');
      removeUser();
      return;
    }

    try {
      console.log('Refreshing access token...');
      const res: any = await api.POST(
        'auth/client/token',
        { refreshToken }
      );
  
      cookie.save('accessToken', res.token, {
        path: '/',
        expires: new Date(Date.now() + (5 * 60 * 1000))
      });
      console.log('Access token refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh token:', error);
      removeUser();
    }
  }
  return
}

const sendEmail = selectorFamily({
  key: 'sendEmail',
  get: (email: string) => async () => {
    try {
      await sendSignInLinkToEmail(email);
      return {
        success: true
      };
    } catch(err) {
      return {
        result: err,
        success: false
      };
    }
  }
});

const verifyEmail = selectorFamily({
  key: 'verify',
  get: (search: any) => async () => {
    const email = storage.get('userEmail');
    const params = decodeQueryString(search);
    if(email && params && params.apiKey && params.mode === 'signIn') {
      try {
        const res = await isSignInWithEmailLink();
        return res;
      } catch(err) {
        return err;
      }
    } else {
      return null;
    }
  }
});

const changePassword = selectorFamily({
  key:'changePassword',
  get: (params: any) => async () => {
    if (params) {
      try {
        await verifyAuthToken()
        const res: any = await api.PUT('auth/client/change-password', params);
        return {
          result: res,
          success: true
        };
      } catch (error) {
        return {
          result: error,
          success: false
        };
      }
    }
  }
});

const updateProfile = selectorFamily({
  key:'updateProfile',
  get: (params: any) => async () => {
    if (params) {
      try {
        await verifyAuthToken()
        const res: any = await api.PUT('auth/client/profile', params);
        // Don't save params directly - let getProfile refetch the updated user from server
        // The atom effect will handle saving when setUser is called after getProfile refetches
        return {
          result: res,
          success: true
        };
      } catch (error) {
        return {
          result: error,
          success: false
        };
      }
    }
  }
});

const logout = selector({
  key:'logout',
  get: async ({ get }) => {
    const userAtom = get(userInfo);
    const data = {
      refreshToken: userAtom.refreshToken
    }
    try {
      await verifyAuthToken()
      const res: any = await api.PUT('auth/client/logout', data);
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      cookie.remove('accessToken', { path: '/' });
      return {
        result: res,
        success: true
      };
    } catch (error) {
      return {
        result: error,
        success: false
      };
    }
  }
});

const getProfile = selector({
  key: 'getProfile',
  get: async ({ get }) => {
    try {
      get(updateUser)
      await verifyAuthToken()
      const res: any = await api.GET(`auth/client/info`);
      return {
        result: res,
        success: true
      }
    } catch (error) {
      return {
        result: [],
        success: false
      }
    }
  }
});

const resetPasswordRequest = selectorFamily({
  key: 'resetPasswordRequest',
  get: ({ Contact_Email }: any) => async () => {
    try {
      const res: any = await api.PUT('auth/client/password/resetrequest', { Contact_Email });
      return {
        result: res,
        success: true
      };
    } catch(err) {
      return {
        result: err,
        success: false
      };
    }
  }
});

const resetPassword = selectorFamily({
  key: 'resetPassword',
  get: ({ new_password, token }: any) => async () => {
    try {
      const res: any = await api.PUT('auth/client/resetpassword', { new_password, token });
      return {
        result: res,
        success: true
      };
    } catch(err) {
      return {
        result: err,
        success: false
      };
    }
  }
});

const masterLogin = selectorFamily({
  key: 'masterLogin',
  get: ({ token }: any) => async () => {
    try {
      
      const res: any = await api.POST('auth/client/master-login', { token });

      storage.set('user', JSON.stringify(res));
      storage.set('refreshToken', res.refreshToken);
      cookie.save('accessToken', res.token, {
        path: '/',
        expires: new Date(Date.now() + (5 * 60 * 1000))
      });
      
      return {
        result: res,
        success: true
      };
    } catch(err) {
      return {
        result: err,
        success: false
      };
    }
  }
});

export {
  checkAuthType,
  loginWithPassword,
  loginWithoutPassword,
  refreshAuthToken,
  sendEmail,
  verifyEmail,
  changePassword,
  updateProfile,
  logout,
  getProfile,
  resetPasswordRequest,
  resetPassword,
  verifyAuthToken,
  masterLogin
};
